import React, { useRef, useId } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CloudUploadOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

// Check for .dcm, .dicom, or files with no extension (no dot in the name)
const isDicom = (file) => {
  if (!file?.name) return false;
  const name = file.name.toLowerCase();
  return name.endsWith('.dcm') || name.endsWith('.dicom') || !name.includes('.');
};

const DicomDropzone = ({ disabled, hasFile, onFilesAdded }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const inputRef = useRef(null);
  const inputId = useId();

  const processFiles = (fileList) => {
    if (!fileList || !fileList.length) return;

    const files = Array.from(fileList);
    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      if (isDicom(file)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
      }
    });

    if (invalidFiles.length > 0) {
      enqueueSnackbar(
        t(
          'DicomDropzone.someRejected',
          `Preskočené súbory (${invalidFiles.length}): Podporované sú iba .dcm / .dicom alebo súbory bez prípony.`
        ),
        { variant: 'warning' }
      );
    }

    if (validFiles.length > 0) {
      // Pass the whole array of valid files up to the parent
      onFilesAdded(validFiles);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    processFiles(e.dataTransfer?.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleInputChange = (e) => {
    if (disabled) return;
    processFiles(e.target.files);
    e.target.value = ''; // Reset input so the same files can be selected again if needed
  };

  const handleClick = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onClick={handleClick}
      sx={(theme) => ({
        border: '2px dashed',
        borderColor: 'divider',
        borderRadius: 3,
        p: 4,
        textAlign: 'center',
        mb: 2,
        bgcolor: 'background.paper',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: disabled ? 'divider' : 'primary.main',
          bgcolor: disabled ? 'background.paper' : alpha(theme.palette.primary.main, 0.05),
        }
      })}
    >
      <Stack alignItems="center" spacing={1.5}>
        <CloudUploadOutlined sx={{ fontSize: 48, color: 'primary.main', opacity: 0.8 }} />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t('DicomDropzone.title', 'Presuňte sem DICOM súbory alebo kliknite pre výber')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('DicomDropzone.subtitle', 'Môžete vybrať aj viacero súborov naraz.')}
          </Typography>
        </Box>
      </Stack>

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        multiple // <-- Allows selecting multiple files
        // We removed 'accept' entirely so the OS file picker shows files without extensions!
        style={{ display: 'none' }}
        onChange={handleInputChange}
        disabled={disabled}
      />
    </Box>
  );
};

export default DicomDropzone;